import { t, Trans } from "@lingui/macro";
import { DeviceMobile, Download, Export, Check, X } from "@phosphor-icons/react";
import { Button, TooltipRoot, TooltipContent, TooltipTrigger } from "@stylingcv/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";

import { usePWAInstall } from "@/client/hooks/use-pwa-install";

interface InstallAppButtonProps {
  variant?: "default" | "outline" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showLabel?: boolean;
}

interface FloatingInstallButtonProps {
  className?: string;
  position?: "bottom-left" | "bottom-right";
  delay?: number;
}

export const InstallAppButton = ({
  variant = "outline",
  size = "sm",
  className = "",
  showLabel = true,
}: InstallAppButtonProps) => {
  const { isInstallable, isInstalled, isInstalling, install, isIOSSafari, canShowInstallUI } =
    usePWAInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Don't show if already installed or not installable
  if (!canShowInstallUI) {
    return null;
  }

  // Already installed
  if (isInstalled) {
    return (
      <Button variant="ghost" size={size} className={className} disabled>
        <Check className="size-4" />
        {showLabel && <span className="ml-2">{t`App Installed`}</span>}
      </Button>
    );
  }

  // iOS Safari - show manual instructions
  if (isIOSSafari) {
    return (
      <>
        <TooltipRoot>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size={size}
              className={className}
              onClick={() => setShowIOSInstructions(true)}
            >
              <Download className="size-4" />
              {showLabel && <span className="ml-2">{t`Install App`}</span>}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t`Install Stylion on your device`}</p>
          </TooltipContent>
        </TooltipRoot>

        {/* iOS Instructions Modal */}
        <AnimatePresence>
          {showIOSInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowIOSInstructions(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-sm rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                    <DeviceMobile className="size-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{t`Install Stylion`}</h3>
                </div>

                <p className="mb-4 text-sm text-muted-foreground">
                  <Trans>To install on your iOS device:</Trans>
                </p>

                <ol className="mb-6 space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      1
                    </span>
                    <span>
                      <Trans>
                        Tap the <Export className="inline size-4" /> Share button in Safari
                      </Trans>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      2
                    </span>
                    <span>
                      <Trans>Scroll down and tap "Add to Home Screen"</Trans>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      3
                    </span>
                    <span>
                      <Trans>Tap "Add" to confirm</Trans>
                    </span>
                  </li>
                </ol>

                <Button className="w-full" onClick={() => setShowIOSInstructions(false)}>
                  {t`Got it`}
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Standard install button (Chrome, Edge, etc.)
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={install}
          disabled={isInstalling || !isInstallable}
        >
          {isInstalling ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Download className="size-4" />
            </motion.div>
          ) : (
            <Download className="size-4" />
          )}
          {showLabel && (
            <span className="ml-2">{isInstalling ? t`Installing...` : t`Install App`}</span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{t`Install Stylion on your device for quick access`}</p>
      </TooltipContent>
    </TooltipRoot>
  );
};

/**
 * Floating install button that appears after a delay
 * Shows a dismissible prompt to install the app
 */
/**
 * Global PWA Install Prompter
 * Handles both:
 * 1. Passive engagement (floating button after delay)
 * 2. Active engagement (auto-trigger via ?install=true deep link)
 */
export const FloatingInstallButton = ({
  className = "",
  position = "bottom-right",
  delay = 10000,
}: FloatingInstallButtonProps) => {
  const { 
    isInstallable, 
    isInstalled, 
    isInstalling, 
    install, 
    isIOSSafari, 
    canShowInstallUI, 
    shouldAutoShowInstall 
  } = usePWAInstall();

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [mode, setMode] = useState<"floating" | "modal">("floating");

  useEffect(() => {
    // Priority: Deep Link Auto-Show
    if (shouldAutoShowInstall && canShowInstallUI && !isInstalled) {
      setMode("modal");
      setIsVisible(true);
      
      // If iOS, show instructions immediately
      if (isIOSSafari) {
        setShowIOSInstructions(true);
      }
      return;
    }

    // Secondary: Passive Timer
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    const timer = setTimeout(() => {
      if (canShowInstallUI && !isInstalled) {
        setMode("floating");
        setIsVisible(true);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [canShowInstallUI, isInstalled, delay, shouldAutoShowInstall, isIOSSafari]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem("pwa-install-dismissed", "true");
  };

  const handleInstall = async () => {
    if (isIOSSafari) {
      setShowIOSInstructions(true);
      return;
    }

    const result = await install();
    if (result.success) {
      setIsVisible(false);
    }
  };

  if (!canShowInstallUI || isInstalled || (!isVisible && !showIOSInstructions)) {
    return null;
  }

  // Modal Mode (Deep Link / Premium Trigger)
  if (mode === "modal" && isVisible) {
    return (
      <AnimatePresence>
        <motion.div
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           exit={{ opacity: 0 }}
           className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
           onClick={handleDismiss}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm overflow-hidden rounded-3xl bg-background shadow-2xl ring-1 ring-white/10"
          >
            <div className="relative h-32 bg-gradient-to-br from-blue-600 to-indigo-600">
               <div className="absolute inset-0 bg-[url('/images/grid-pattern.svg')] opacity-20" />
               <div className="absolute -bottom-12 left-1/2 flex size-24 -translate-x-1/2 items-center justify-center rounded-2xl bg-white shadow-xl">
                 <img src="/icon/app-icon-192.svg" alt="App Icon" className="size-20 rounded-xl" />
               </div>
            </div>
            
            <div className="pt-16 pb-8 px-6 text-center">
              <h3 className="text-2xl font-bold tracking-tight">{t`Get the App`}</h3>
              <p className="mt-2 text-muted-foreground">
                {t`Install Stylion for the best experience. Offline access, notifications, and faster performance.`}
              </p>

              <div className="mt-8 flex flex-col gap-3">
                <Button size="lg" className="w-full rounded-xl text-base" onClick={handleInstall}>
                  {isInstalling ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, ease: "linear", duration: 1 }} className="mr-2">
                       <Download className="size-5" />
                    </motion.div>
                  ) : <Download className="mr-2 size-5" />}
                  {t`Install Now`}
                </Button>
                <Button variant="ghost" className="w-full rounded-xl" onClick={handleDismiss}>
                  {t`Maybe Later`}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Re-use iOS instructions if needed */}
        {showIOSInstructions && (
          <IOSInstructionsModal onClose={() => setShowIOSInstructions(false)} />
        )}
      </AnimatePresence>
    );
  }

  // Floating Mode (Passive)
  const positionClasses = position === "bottom-left" ? "bottom-4 left-4" : "bottom-4 right-4";

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed ${positionClasses} z-50 w-full max-w-[320px] sm:w-auto ${className}`}
          >
             <div className="group relative overflow-hidden rounded-2xl border bg-background/95 p-4 shadow-xl backdrop-blur-md transition-all hover:shadow-2xl dark:bg-card/95">
               <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
               
               <div className="flex gap-4">
                 <button onClick={handleInstall} className="shrink-0 transition-transform active:scale-95">
                   <div className="flex size-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-indigo-50 shadow-inner dark:from-blue-900/50 dark:to-indigo-900/50">
                     <img src="/icon/app-icon-192.svg" alt="Icon" className="size-10 rounded-lg shadow-sm" />
                   </div>
                 </button>

                 <div className="flex-1 min-w-0">
                   <div className="flex items-start justify-between">
                     <h4 className="font-semibold leading-tight">{t`Install App`}</h4>
                     <button onClick={handleDismiss} className="text-muted-foreground/50 hover:text-foreground">
                       <X className="size-4" />
                     </button>
                   </div>
                   <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                     {t`Add Stylion to your desktop for a native experience.`}
                   </p>
                   
                   <div className="mt-3 flex gap-2">
                     <Button size="sm" onClick={handleInstall} className="h-8 rounded-lg px-4 text-xs">
                        {t`Install`}
                     </Button>
                   </div>
                 </div>
               </div>
             </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showIOSInstructions && (
        <IOSInstructionsModal onClose={() => setShowIOSInstructions(false)} />
      )}
    </>
  );
};

// Refactored Helper Component for Cleaner Code
const IOSInstructionsModal = ({ onClose }: { onClose: () => void }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-4 sm:items-center"
    onClick={onClose}
  >
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="w-full max-w-sm rounded-t-3xl bg-background p-6 shadow-2xl sm:rounded-3xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-muted" />
      
      <div className="mb-6 flex items-center gap-4">
        <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
          <DeviceMobile className="size-7 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-bold">{t`Install for iOS`}</h3>
          <p className="text-sm text-muted-foreground">{t`Add to Home Screen`}</p>
        </div>
      </div>

      <ol className="mb-8 space-y-4">
        <li className="flex items-center gap-4 rounded-xl bg-muted/50 p-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">1</span>
          <span className="text-sm font-medium"><Trans>Tap the <Export className="mx-1 inline size-4 text-primary" /> Share button</Trans></span>
        </li>
        <li className="flex items-center gap-4 rounded-xl bg-muted/50 p-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">2</span>
          <span className="text-sm font-medium"><Trans>Select "Add to Home Screen"</Trans></span>
        </li>
      </ol>

      <Button className="w-full py-6 text-lg rounded-xl" onClick={onClose}>
        {t`Got it`}
      </Button>
    </motion.div>
  </motion.div>
);
